// 講師用：出勤不可コマ提出画面（全クリア・個別クリア・曜日ごとの選択/解除ボタン追加）

import React, { useEffect, useState } from "react";
import { getRequest, postRequest } from "../../apiWrapper";
import { toast } from "react-toastify";
import { formatDate, getDatesBetween, toJSTDate } from "../../utils/formatDate";
import { FaArrowUp } from "react-icons/fa";

const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
const periods = [1, 2, 3, 4, 5, 6, 7];
const STORAGE_KEY = "lastUnavailableMap";
const TEMP_SAVE_KEY = "tempUnavailableMap";

const TeacherUnavailabilityInput = () => {
  const [entryWindow, setEntryWindow] = useState(null);
  const [unavailableMap, setUnavailableMap] = useState({});
  const [weeklyPattern, setWeeklyPattern] = useState({});
  const [filterDay, setFilterDay] = useState("all");
  const [weeklyShifts, setWeeklyShifts] = useState([]);
  const [regularHolidays, setRegularHolidays] = useState([]);
  const [specialHolidays, setSpecialHolidays] = useState([]);
  const [latestSubmission, setLatestSubmission] = useState(null);
  const [submissionHistory, setSubmissionHistory] = useState([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        let windows = [];
        try {
          const winRes = await getRequest("shiftEntryWindows");
          windows = winRes.windows || [];
        } catch { }
        if (windows.length) {
          windows.sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
          setEntryWindow(windows[0]);
        }
        let shiftsRes = { shifts: [] };
        let regRes = { holidays: [] };
        let specRes = { holidays: [] };
        try {
          shiftsRes = await getRequest("teacherWeeklyShifts");
        } catch (err) {
          console.error("teacherWeeklyShifts 取得失敗:", err);
        }
        try {
          regRes = await getRequest("schoolRegularHolidays");
        } catch (err) {
          console.error("schoolRegularHolidays 取得失敗:", err);
        }
        try {
          specRes = await getRequest("schoolSpecialHolidays");
        } catch (err) {
          console.error("schoolSpecialHolidays 取得失敗:", err);
        }
        setWeeklyShifts(shiftsRes.shifts);
        setRegularHolidays(regRes.holidays);
        setSpecialHolidays(specRes.holidays);

        try {
          const latestRes = await getRequest("teacherUnavailabilitySubmissions/latest");
          setLatestSubmission(latestRes.submission?.submitted_at || null);
        } catch {
          setLatestSubmission(null);
        }

        try {
          const historyRes = await getRequest("teacherUnavailabilitySubmissions");
          setSubmissionHistory(historyRes.submissions || []);
        } catch (err) {
          console.error("履歴取得エラー", err);
        }

        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const obj = JSON.parse(saved);
          const restored = {};
          Object.entries(obj).forEach(([d, arr]) => {
            restored[d] = new Set(arr);
          });
          setUnavailableMap(restored);
        }
      } catch (err) {
        console.error("初期データ一括取得エラー", err);
        toast.error("初期データの取得に失敗しました");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const handleLoadFromHistory = async () => {
    if (!selectedSubmissionId) return;
    try {
      const data = await getRequest(`teacherUnavailabilities/bySubmission/${selectedSubmissionId}`);
      const restored = {};

      data.unavailabilities.forEach(item => {
        const dateStr = toJSTDate(new Date(item.date)).toISOString().split("T")[0]; // ← JST補正済み
        if (!restored[dateStr]) restored[dateStr] = new Set();
        restored[dateStr].add(item.period);
      });

      const matched = submissionHistory.find(s => s.id === selectedSubmissionId);
      if (matched && matched.entry_window_id === entryWindow?.id) {
        setUnavailableMap(restored);
        toast.success("履歴を反映しました");
      } else {
        toast.error("この履歴は現在の提出期間と一致しません");
      }
    } catch (err) {
      console.error("履歴読み込み失敗", err);
      toast.error("読み込みに失敗しました");
    }
  };

  const getAllDates = () => {
    if (loading || !entryWindow?.start_date || !entryWindow?.end_date) return [];
    return getDatesBetween(entryWindow.start_date, entryWindow.end_date);
  };

  const toggleUnavailable = (date, p) => {
    setUnavailableMap(prev => {
      const curr = new Set(prev[date] || []);
      if (curr.has(p)) curr.delete(p);
      else curr.add(p);
      return { ...prev, [date]: curr };
    });
  };

  const handleWeeklyToggle = (dow, p) => {
    setWeeklyPattern(prev => {
      const s = new Set(prev[dow] || []);
      if (s.has(p)) s.delete(p);
      else s.add(p);
      return { ...prev, [dow]: Array.from(s) };
    });
  };

  const clearWeeklyPattern = (dow) => {
    setWeeklyPattern(prev => ({ ...prev, [dow]: [] }));
  };

  const applyWeeklyPattern = () => {
    const map = { ...unavailableMap };
    getAllDates().forEach(date => {
      const dow = new Date(date).getDay();
      const arr = weeklyPattern[dow] || [];
      if (!arr.every(p => isHoliday(date, p))) {
        map[date] = new Set(arr);
      }
    });
    setUnavailableMap(map);
  };

  const applyDayPattern = (date, periods) => {
    setUnavailableMap(prev => {
      const updated = new Set(prev[date] || []);
      periods.forEach(p => {
        if (!isHoliday(date, p)) updated.add(p);
      });
      return { ...prev, [date]: updated };
    });
  };

  const clearDay = (date) => {
    setUnavailableMap(prev => {
      const copy = { ...prev };
      delete copy[date];
      return copy;
    });
  };

  const clearAll = () => {
    if (window.confirm("本当にすべての選択をクリアしますか？")) {
      setUnavailableMap({});
    }
  };

  const saveTemp = () => {
    const temp = {};
    Object.entries(unavailableMap).forEach(([d, set]) => {
      temp[d] = Array.from(set);
    });
    localStorage.setItem(TEMP_SAVE_KEY, JSON.stringify(temp));
    toast.success("一時保存しました");
  };

  const loadTemp = () => {
    const saved = localStorage.getItem(TEMP_SAVE_KEY);
    if (!saved) {
      toast.warn("保存データがありません");
      return;
    }
    if (!window.confirm("保存された内容を復元しますか？現在の選択は上書きされます。")) return;
    const obj = JSON.parse(saved);
    const restored = {};
    Object.entries(obj).forEach(([d, arr]) => {
      restored[d] = new Set(arr);
    });
    setUnavailableMap(restored);
    toast.success("復元しました");
  };

  const isHoliday = (date, p) => {
    const dow = new Date(date).getDay();
    if (regularHolidays.some(h => h.day_of_week === dow && (h.period === null || h.period === p))) return true;
    if (specialHolidays.some(h => {
      const holidayStr = typeof h.date === "string"
        ? toJSTDate(h.date).toISOString().split("T")[0]
        : toJSTDate(new Date(h.date)).toISOString().split("T")[0];
      return holidayStr === date && (h.period === null || h.period === p);
    })) return true;
    return false;
  };

  const getConflictWarnings = () => {
    const warns = [];
    Object.entries(unavailableMap).forEach(([date, set]) => {
      set.forEach(p => {
        weeklyShifts.forEach(ws => {
          if (ws.day_of_week === new Date(date).getDay() && ws.period === p) {
            warns.push(`${formatDate(date)} の ${p}講`);
          }
        });
      });
    });
    return warns;
  };

  const handleSubmit = async () => {
    // 出勤不可の選択が1つもない場合は警告して処理中断
    const hasAnySelection = Object.values(unavailableMap).some(set => set.size > 0);
    if (!hasAnySelection) {
      toast.warn("出勤不可のコマが1つも選択されていません。");
      return;
    }

    const unavailabilities = [];
    Object.entries(unavailableMap).forEach(([date, set]) => set.forEach(p => unavailabilities.push({ date, period: p })));
    const warnings = getConflictWarnings();
    if (warnings.length && !window.confirm(`出勤予定と重なるコマがあります：\n${warnings.join("\n")}\nそれでも提出しますか？`)) return;
    try {
      await postRequest("teacherUnavailabilities/bulk", {
        entry_window_id: entryWindow.id,
        unavailabilities
      });
      const sav = {};
      Object.entries(unavailableMap).forEach(([d, set]) => sav[d] = Array.from(set));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(sav));
      toast.success("提出が完了しました");
    } catch (err) {
      console.error("提出エラー", err);
      toast.error("提出に失敗しました");
    }
  };

  if (loading) return <p className="p-4 text-center">提出期間を読み込み中…</p>;

  return (
    <div className="p-4 pt-16 pb-28 max-w-4xl mx-auto relative">
      {/* 曜日絞り込みボタン（右側固定・背景白・日付正対応） */}
      <div className="fixed right-0 top-1/2 transform -translate-y-1/2 bg-white border rounded-l shadow-md z-50">
        {["全", "月", "火", "水", "木", "金", "土"].map((label, i) => {
          const dayValue = i === 0 ? "all" : String(i);
          const isSelected = filterDay === dayValue;
          return (
            <button
              key={label}
              className={`block w-10 p-1 border-b text-sm ${isSelected ? "bg-blue-500 text-white" : "bg-white text-black"}`}
              onClick={() => setFilterDay(dayValue)}
            >
              {label}
            </button>
          );
        })}
      </div>

      <h1 className="text-xl font-bold mb-2">出勤不可コマの提出</h1>

      {entryWindow && (
        <p className="text-sm text-blue-700 mb-2">
          提出対象期間：{formatDate(entryWindow.start_date)} ～ {formatDate(entryWindow.end_date)}
        </p>
      )}

      {/* 提出履歴セレクトと反映ボタン */}
      <div className="flex items-center gap-2 mb-4">
        <select
          className="p-2 border rounded"
          value={selectedSubmissionId}
          onChange={(e) => setSelectedSubmissionId(e.target.value)}
        >
          <option value="">過去の提出履歴を選択</option>
          {submissionHistory.map(sub => (
            <option key={sub.id} value={sub.id}>
              {formatDate(sub.submitted_at)}（{sub.id.slice(0, 8)}）
            </option>
          ))}
        </select>
        <button
          onClick={handleLoadFromHistory}
          className="px-4 py-2 bg-blue-500 text-white rounded"
        >
          表示
        </button>
      </div>

      {latestSubmission && (
        <p className="text-sm text-gray-600 mb-4">最終提出：{new Date(latestSubmission).toLocaleString("ja-JP", { timeZone: "Asia/Tokyo" })}</p>
      )}

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={saveTemp} className="px-4 py-2 bg-blue-500 text-white rounded">一時保存</button>
        <button onClick={loadTemp} className="px-4 py-2 bg-blue-500 text-white rounded">保存を復元</button>
        <button onClick={clearAll} className="px-4 py-2 bg-red-600 text-white rounded">全クリア</button>
      </div>

      <div className="bg-white p-4 rounded shadow mb-4">
        <h2 className="font-semibold mb-2">曜日ごとの一括選択</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4, 5, 6].map(dow => (
            <div key={dow} className="border p-2 rounded">
              <div className="flex justify-between items-center mb-1">
                <h4 className="font-semibold">{weekdays[dow]}曜</h4>
                <div className="flex gap-2">
                  <button onClick={() => setWeeklyPattern(prev => ({ ...prev, [dow]: [...periods] }))} className="text-xs text-blue-600">全選択</button>
                  <button onClick={() => clearWeeklyPattern(dow)} className="text-xs text-red-600">クリア</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {periods.map(p => (
                  <label key={p} className="flex items-center">
                    <input type="checkbox" checked={(weeklyPattern[dow] || []).includes(p)} onChange={() => handleWeeklyToggle(dow, p)} className="mr-1" />{p}講
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button onClick={applyWeeklyPattern} className="mt-3 px-4 py-2 bg-blue-500 text-white rounded">
          このパターンを反映
        </button>
      </div>

      <div className="space-y-4">
        {getAllDates().map(date => {
          const dow = new Date(date).getDay();
          if (filterDay !== "all" && filterDay !== String(dow)) return null;
          const isWholeHoliday = periods.every(p => isHoliday(date, p));
          return (
            <div key={date} className="bg-white p-3 rounded shadow">
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">{new Date(date).toLocaleDateString("ja-JP", { timeZone: "Asia/Tokyo" })}（{weekdays[dow]}）</span>
                <div className="flex gap-2">
                  {!isWholeHoliday && (
                    <button className="text-xs text-blue-600 border px-2 py-1 rounded" onClick={() => applyDayPattern(date, periods)}>全選択</button>
                  )}
                  {!isWholeHoliday && (
                    <button className="text-xs text-red-600 border px-2 py-1 rounded" onClick={() => clearDay(date)}>クリア</button>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {periods.map(p => {
                  const isUn = unavailableMap[date]?.has(p);
                  const isDis = isHoliday(date, p);
                  return (
                    <button
                      key={p}
                      className={`w-10 h-10 rounded border text-sm ${isUn ? "bg-red-500 text-white" : "bg-blue-100 text-black"} ${isDis ? "opacity-50 pointer-events-none" : ""}`}
                      onClick={() => toggleUnavailable(date, p)}
                    >{p}</button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="fixed bottom-0 left-0 w-full bg-white border-t py-3 px-4 shadow-inner z-10">
        <button onClick={handleSubmit} className="w-full py-3 bg-green-600 text-white text-lg rounded hover:bg-green-700">
          提出する
        </button>
      </div>

      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className="fixed bottom-20 right-4 bg-blue-600 text-white p-3 rounded-full shadow-md hover:bg-blue-700 z-20"
        aria-label="ページ上部へ戻る"
      >
        <FaArrowUp />
      </button>
    </div>
  );
};

export default TeacherUnavailabilityInput;